import axios from 'axios';

// Extend the AxiosRequestConfig type to include our custom properties
declare module 'axios' {
  interface InternalAxiosRequestConfig {
    cached?: unknown;
  }
}

/**
 * Simple in-memory cache for GET requests
 * @type {Map<string, {data: unknown, timestamp: number}>}
 */
const apiCache = new Map<string, {data: unknown, timestamp: number}>();

/**
 * Cache expiration time in milliseconds (5 minutes)
 */
const CACHE_EXPIRATION = 5 * 60 * 1000;

/**
 * Generates a cache key from request config
 * @param {string} url - The request URL
 * @param {Record<string, unknown>} params - The request params
 * @returns {string} - The cache key
 */
function generateCacheKey(url: string, params?: Record<string, unknown>): string {
  return `${url}${params ? `?${JSON.stringify(params)}` : ''}`;
}

/**
 * Clears the entire API cache
 */
export function clearApiCache(): void {
  apiCache.clear();
}

/**
 * Type representing the status of a toast notification
 * @type {ToastStatus}
 */
type ToastStatus = 'error' | 'success' | 'warning' | 'info' | 'loading';

/**
 * Custom error type for API errors
 * @interface ApiErrorType
 */
export interface ApiErrorType {
  message: string;
  severity?: ToastStatus;
  requiresVerification?: boolean;
  redirect?: string;
  willRedirect?: boolean;
}

/**
 * Custom error class for API errors
 * @class ApiError
 * @extends Error
 * @implements {ApiErrorType}
 */
export class ApiError extends Error implements ApiErrorType {
  severity?: ToastStatus;
  requiresVerification?: boolean;
  redirect?: string;
  willRedirect?: boolean;
  constructor(message: string, severity?: string, requiresVerification?: boolean, redirect?: string) {
    const validSeverity = (s?: string): ToastStatus => {
      switch(s) {
        case 'error':
        case 'success':
        case 'warning':
        case 'info':
        case 'loading':
          return s;
        default:
          return 'error';
      }
    };
    super(message);
    this.name = 'ApiError';
    this.severity = validSeverity(severity);
    this.requiresVerification = requiresVerification;
    this.redirect = redirect;
    this.willRedirect = !!redirect;
  }
}


// EMERGENCY REDIRECT FUNCTION - Bypass all normal flow
function emergencyRedirect(url: string): void {
  window.location.href = url;
}

/**
 * Axios instance configured for API requests
 * @constant {AxiosInstance} api
 */
const api = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json',
  },
  transformRequest: [(data, headers) => {
    
    if (data instanceof FormData) {
      delete headers['Content-Type'];
      return data;
    }
    if (data instanceof Blob || data instanceof File) {
      return data;
    }
    return JSON.stringify(data);
  }],
  transformResponse: [(data) => {
    
    try {
      // Check for redirect in raw response
      if (typeof data === 'string') {
        try {
          const jsonData = JSON.parse(data);
          
          if (jsonData && jsonData.redirect) {
            // Force immediate redirect
            emergencyRedirect(jsonData.redirect);
          }
        } finally {/*Do nothing*/}
      }
      return JSON.parse(data);
    } catch {
      return data;
    }
  }],
});

// Request interceptor for adding auth token and handling cache
api.interceptors.request.use(
  (config) => {
    
    
    if (config.url?.startsWith('http')) {
      
      return config;
    }
    
    const token = localStorage.getItem('token');
    if (token) {
      
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Check cache for GET requests
    if (config.method?.toLowerCase() === 'get' && config.url) {
      const cacheKey = generateCacheKey(config.url, config.params);
      const cachedResponse = apiCache.get(cacheKey);
      
      if (cachedResponse && (Date.now() - cachedResponse.timestamp) < CACHE_EXPIRATION) {
        // Set a flag to indicate this request should use cached data
        config.headers['x-use-cache'] = 'true';
        // Store the cached data in the config for the response interceptor to use
        config.cached = cachedResponse.data;
      } else if (cachedResponse) {
        apiCache.delete(cacheKey);
      }
    }
    
    return config;
  },
  (error) => {
    
    return Promise.reject(error);
  }
);

// Response interceptor for handling errors and caching
api.interceptors.response.use(
  (response) => {
    
    
    // Check for redirect in successful responses too
    if (response.data && response.data.redirect) {
      
      emergencyRedirect(response.data.redirect);
    }
    
    // If this is a cached response, return it directly
    if (response.config.headers?.['x-use-cache'] === 'true' && response.config.cached) {
      
      // Create a new response object with the cached data
      const cachedResponse = { ...response, data: response.config.cached };
      return cachedResponse;
    }
    
    // Cache GET responses
    if (response.config.method?.toLowerCase() === 'get' && response.config.url) {
      const cacheKey = generateCacheKey(response.config.url, response.config.params);
      apiCache.set(cacheKey, {
        data: response.data,
        timestamp: Date.now()
      });
      
    }
    
    // Clear cache for POST/PUT/DELETE requests
    if (['post', 'put', 'delete'].includes(response.config.method?.toLowerCase() || '')) {
      
      clearApiCache();
    }
    
    return response;
  },
  (error) => {
    
     
    // Skip error handling for S3 requests
    if (error.config?.url?.startsWith('http')) {
      
      return Promise.reject(new ApiError('Failed to upload file. Please try again.'));
    }

    // Handle 401 unauthorized responses
    if (error.response?.status === 401) {
      const token = localStorage.getItem('token');
      if (token) {
        
        localStorage.removeItem('token');
        window.dispatchEvent(new CustomEvent('auth:expired'));
        window.location.reload();
        return Promise.reject(new ApiError('Session expired. Please log in again.', 'error'));
      } else {
        
        return Promise.reject(new ApiError('Authentication required. Please log in.', 'error'));
      }
    }

    // Handle verification required
    if (error.response?.status === 403 && error.response.headers['x-require-verification']) {
      
      window.dispatchEvent(new CustomEvent('security:verification-required'));
      return Promise.reject(new ApiError(
        'Please complete the captcha to continue',
        'warning',
        true
      ));
    }

    // Extract error data
    const errorData = error.response?.data;
    
    
    // CRITICAL: Handle redirect IMMEDIATELY with direct window.location.href
    if (errorData && errorData.redirect) {
      
      
      
      // Force immediate redirect - no events, no promises
      emergencyRedirect(errorData.redirect);
      
      // This should never be reached due to the redirect
      return Promise.reject(new ApiError('Redirecting...', 'info'));
    }

    // Handle network errors
    if (!error.response) {
      
      return Promise.reject(new ApiError(
        'Network error. Please check your connection and try again.',
        'error'
      ));
    }

    // Handle all other errors
    const message = errorData?.message || 'An error occurred';
    const severity = errorData?.severity || 'error';
    
    return Promise.reject(new ApiError(message, severity, false));
  }
);

// Clear cache on error responses for POST/PUT/DELETE
api.interceptors.response.use(undefined, (error) => {
  if (error.config && ['post', 'put', 'delete'].includes(error.config.method?.toLowerCase() || '')) {
    
    clearApiCache();
  }
  return Promise.reject(error);
});

// Add global error handler to catch unhandled promise rejections
window.addEventListener('unhandledrejection', (event) => {
  
  if (event.reason instanceof ApiError && event.reason.redirect) {
    
    emergencyRedirect(event.reason.redirect);
  }
});

// Add a global fetch interceptor to catch 403 responses
const originalFetch = window.fetch;
window.fetch = async function(input, init) {
  const response = await originalFetch(input, init);
  
  // Clone the response so we can read the body
  const clonedResponse = response.clone();
  
  // Check for 403 status
  if (response.status === 403) {
    
    try {
      // Try to parse the response as JSON
      const data = await clonedResponse.json();
      
      
      if (data.redirect) {
        
        emergencyRedirect(data.redirect);
      }
    } catch {
      // Do nothing if the response is not JSON
    }
  }
  
  return response;
};

// Add a global event listener for AJAX responses
document.addEventListener('DOMContentLoaded', () => {
  
  // Add a MutationObserver to watch for XHR responses in the DOM
  // This is a fallback method that doesn't rely on modifying prototypes
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'childList') {
        // Check for error messages that might contain redirect info
        const errorElements = document.querySelectorAll('.error-message, .alert-error, [role="alert"]');
        for (const element of errorElements) {
          const text = element.textContent || '';
          if (text.includes('resource limit') || text.includes('maximum number')) {
            
            
            // Check if there's a redirect link
            const links = element.querySelectorAll('a[href^="/resource-limit"]');
            if (links.length > 0) {
              const redirectUrl = links[0].getAttribute('href');
              if (redirectUrl) {
                
                emergencyRedirect(redirectUrl);
              }
            }
          }
        }
      }
    }
  });
  
  observer.observe(document.body, { childList: true, subtree: true });
});

export default api;
